import { useState, useEffect, useCallback, useRef } from 'react';
import { Link } from 'react-router-dom';
import SEOHead from '../components/SEOHead';
import AdSense from '../components/AdSense';
import OptionPriceChart from '../components/charts/OptionPriceChart';
import SpotPriceChart from '../components/charts/SpotPriceChart';
import PDEStackedChart from '../components/charts/PDEStackedChart';
import DualAxisLineChart from '../components/charts/DualAxisLineChart';
import katex from 'katex';
import 'katex/dist/katex.min.css';

function Tex({ children, display = false }) {
  const html = katex.renderToString(children, { displayMode: display, throwOnError: false });
  return <span dangerouslySetInnerHTML={{ __html: html }} />;
}

const API = process.env.REACT_APP_API_URL || '';

// ─── Helpers ──────────────────────────────────────────────────────────────────

function Card({ title, children, className = '' }) {
  return (
    <div className={`bg-surface border border-surface rounded-clay p-4 shadow-clay dark:shadow-clay-dark ${className}`}>
      {title && <h3 className="text-base font-semibold text-neutral-900 dark:text-white mb-3">{title}</h3>}
      {children}
    </div>
  );
}

function SectionTitle({ children }) {
  return (
    <h2 className="text-sm font-semibold text-neutral-500 dark:text-neutral-400 uppercase tracking-widest mb-3">
      {children}
    </h2>
  );
}

// ─── Sub-component: Black-Scholes Breakdown ───────────────────────────────────

function BlackScholesBreakdown({ data }) {
  const { inputs, calculations, market_price,
          historical_volatility, theoretical_price_hv, price_difference_hv } = data;

  return (
    <Card title="Black-Scholes Breakdown">
      {theoretical_price_hv != null ? (
        <div className="mb-4">
          <div className="text-xs font-semibold text-neutral-500 dark:text-neutral-400 uppercase tracking-wide mb-2">
            Theoretical vs Market (30d HV = {historical_volatility?.toFixed(1)}%)
          </div>
          <div className="grid grid-cols-3 gap-4">
            <div className="text-center p-3 bg-emerald-50 dark:bg-emerald-900/20 rounded-clay-sm shadow-clay-btn dark:shadow-clay-btn-dark">
              <div className="text-xs text-emerald-600 dark:text-emerald-400 font-medium mb-1">Theoretical (HV)</div>
              <div className="text-2xl font-bold text-emerald-700 dark:text-emerald-300">₹{theoretical_price_hv.toFixed(2)}</div>
            </div>
            <div className="text-center p-3 bg-neutral-50 dark:bg-white/5 rounded-clay-sm shadow-clay-btn dark:shadow-clay-btn-dark">
              <div className="text-xs text-neutral-500 dark:text-neutral-400 font-medium mb-1">Market Price</div>
              <div className="text-2xl font-bold text-neutral-800 dark:text-neutral-100">₹{market_price.toFixed(2)}</div>
            </div>
            <div className="text-center p-3 bg-neutral-50 dark:bg-white/5 rounded-clay-sm shadow-clay-btn dark:shadow-clay-btn-dark">
              <div className="text-xs text-neutral-500 dark:text-neutral-400 font-medium mb-1">Difference</div>
              <div className={`text-2xl font-bold ${
                Math.abs(price_difference_hv) < 2 ? 'text-green-600'
                : price_difference_hv > 0 ? 'text-orange-600'
                : 'text-blue-600'
              }`}>
                {price_difference_hv >= 0 ? '+' : ''}₹{price_difference_hv.toFixed(2)}
              </div>
            </div>
          </div>
          <p className="text-xs text-neutral-400 dark:text-neutral-500 mt-1.5">
            {price_difference_hv < -2
              ? 'Option is expensive vs realized vol — IV exceeds HV (vol premium).'
              : price_difference_hv > 2
              ? 'Option is cheap vs realized vol — IV below HV.'
              : 'Option is fairly priced relative to recent realized volatility.'}
          </p>
        </div>
      ) : (
        <div className="mb-4 p-3 bg-amber-50 dark:bg-amber-900/20 rounded-clay-sm text-xs text-amber-700 dark:text-amber-400 shadow-clay-btn dark:shadow-clay-btn-dark">
          Insufficient price history to compute 30-day HV (need at least 31 trading days).
        </div>
      )}

      <div className="mb-4">
        <div className="text-xs font-semibold text-neutral-500 dark:text-neutral-400 uppercase tracking-wide mb-2">
          Input Variables
        </div>
        <table className="w-full text-sm">
          <tbody className="divide-y divide-neutral-100 dark:divide-white/8">
            {[
              ['S — Spot Price', `₹${inputs.spot.toFixed(2)}`],
              ['K — Strike Price', `₹${inputs.strike.toFixed(2)}`],
              ['T — Time to Expiry', `${inputs.time_to_expiry_days.toFixed(1)} days (${inputs.time_to_expiry_years.toFixed(4)} years)`],
              ['r — Risk-Free Rate', `${(inputs.risk_free_rate * 100).toFixed(2)}%`],
              ['σ — Implied Volatility', `${(inputs.volatility * 100).toFixed(2)}%`],
              ['σ_HV — 30d Historical Vol', historical_volatility != null ? `${historical_volatility.toFixed(2)}%` : 'N/A'],
              ['Type', inputs.option_type === 'CE' ? 'Call (CE)' : 'Put (PE)'],
            ].map(([label, value]) => (
              <tr key={label}>
                <td className="py-1.5 text-neutral-500 dark:text-neutral-400 font-mono text-xs">{label}</td>
                <td className="py-1.5 text-neutral-800 dark:text-neutral-200 font-medium text-right">{value}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      <div>
        <div className="text-xs font-semibold text-neutral-500 dark:text-neutral-400 uppercase tracking-wide mb-2">
          Intermediate Calculations
        </div>
        <table className="w-full text-sm">
          <tbody className="divide-y divide-neutral-100 dark:divide-white/8">
            {[
              ['d₁', 'Log-moneyness + drift over time, scaled by vol. Controls delta.', calculations.d1.toFixed(4)],
              ['d₂', 'd₁ minus vol·√T. Standard deviations from ITM expiry.', calculations.d2.toFixed(4)],
              ['N(d₁)', 'Call delta — probability-weighted exposure to spot price.', calculations.N_d1.toFixed(4)],
              ['N(d₂)', 'Risk-neutral probability the option expires in-the-money.', calculations.N_d2.toFixed(4)],
              ['n(d₁)', 'Standard normal PDF at d₁; used in Vega and Gamma formulas.', calculations.n_d1.toFixed(6)],
              ['e^(-rT)', 'Present value factor — discounts the strike payment to today.', calculations.discount_factor.toFixed(6)],
            ].map(([label, desc, value]) => (
              <tr key={label}>
                <td className="py-1.5">
                  <div className="text-neutral-500 dark:text-neutral-400 font-mono text-xs">{label}</div>
                  <div className="text-neutral-400 dark:text-neutral-500 text-[10px] leading-tight mt-0.5">{desc}</div>
                </td>
                <td className="py-1.5 text-neutral-800 dark:text-neutral-200 font-medium text-right font-mono text-xs">{value}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </Card>
  );
}

// ─── Sub-component: PDE Decomposition Card ────────────────────────────────────

function PDECard({ pde, showEquation = true }) {
  if (!pde) return null;
  const { time_decay_component: T, drift_component: D, convexity_component: G,
          total_pde_price, actual_option_price, pde_error,
          _r: r, _sigma: sigma, _S: S, _delta: delta, _gamma: gamma, _thetaAnnual: thetaAnn } = pde;

  const errorPct = actual_option_price > 0
    ? (pde_error / actual_option_price * 100).toFixed(1)
    : null;

  return (
    <Card title="Option Price Decomposition (Cumulative)">
      {/* PDE equation — hidden when showEquation=false */}
      {showEquation && (
        <>
          <div className="mb-2 overflow-x-auto">
            <Tex display>{String.raw`rV = \frac{\partial V}{\partial t} + rS\,\frac{\partial V}{\partial S} + \frac{1}{2}\sigma^2 S^2\,\frac{\partial^2 V}{\partial S^2}`}</Tex>
          </div>
          <div className="mb-4">
            <Tex display>{String.raw`V \;=\; \underbrace{\frac{1}{r}\,\Theta_{\text{ann}}}_{T} + \underbrace{S\,\Delta}_{D} + \underbrace{\frac{(\sigma S)^2\,\Gamma}{2r}}_{G}`}</Tex>
          </div>
        </>
      )}

      {/* Table */}
      <table className="w-full text-sm border-collapse mb-4">
        <thead>
          <tr className="border-b border-neutral-200 dark:border-neutral-700">
            <th className="text-left text-xs font-medium text-neutral-400 dark:text-neutral-500 pb-2 pr-4 w-24"></th>
            <th className="text-left text-xs font-medium text-red-500 pb-2 px-2">T — Time Decay</th>
            <th className="text-left text-xs font-medium text-blue-500 pb-2 px-2">D — Drift</th>
            <th className="text-left text-xs font-medium text-green-500 pb-2 px-2">G — Convexity</th>
          </tr>
        </thead>
        <tbody>
          <tr className="border-b border-neutral-100 dark:border-neutral-800">
            <td className="text-xs text-neutral-400 dark:text-neutral-500 py-2 pr-4">Formula</td>
            <td className="text-left py-2 px-2"><Tex>{String.raw`\tfrac{1}{r}\Theta_{\text{ann}}`}</Tex></td>
            <td className="text-left py-2 px-2"><Tex>{String.raw`S \cdot \Delta`}</Tex></td>
            <td className="text-left py-2 px-2"><Tex>{String.raw`\tfrac{(\sigma S)^2 \Gamma}{2r}`}</Tex></td>
          </tr>
          {r != null && (
            <tr className="border-b border-neutral-100 dark:border-neutral-800">
              <td className="text-xs text-neutral-400 dark:text-neutral-500 py-2 pr-4">Calculation</td>
              <td className="text-left py-1.5 px-2">
                <Tex>{String.raw`\tfrac{1}{${r.toFixed(3)}} \times ${thetaAnn.toFixed(2)}`}</Tex>
              </td>
              <td className="text-left py-1.5 px-2">
                <Tex>{String.raw`${S.toFixed(0)} \times ${delta.toFixed(4)}`}</Tex>
              </td>
              <td className="text-left py-1.5 px-2">
                <Tex>{String.raw`\tfrac{(${sigma.toFixed(2)} \times ${S.toFixed(0)})^2 \times ${gamma.toFixed(5)}}{2 \times ${r.toFixed(3)}}`}</Tex>
              </td>
            </tr>
          )}
          <tr className="border-b border-neutral-100 dark:border-neutral-800">
            <td className="text-xs text-neutral-400 dark:text-neutral-500 py-2 pr-4">Amount</td>
            <td className="text-left font-mono font-bold text-red-600 dark:text-red-400 py-2 px-2">₹{T.toFixed(2)}</td>
            <td className="text-left font-mono font-bold text-blue-600 dark:text-blue-400 py-2 px-2">₹{D.toFixed(2)}</td>
            <td className="text-left font-mono font-bold text-green-600 dark:text-green-400 py-2 px-2">₹{G.toFixed(2)}</td>
          </tr>
          <tr>
            <td className="text-xs text-neutral-400 dark:text-neutral-500 py-2 pr-4">Factor</td>
            <td className="text-left text-xs text-neutral-600 dark:text-neutral-300 py-2 px-2">Time Value</td>
            <td className="text-left text-xs text-neutral-600 dark:text-neutral-300 py-2 px-2">Distance from Strike</td>
            <td className="text-left text-xs text-neutral-600 dark:text-neutral-300 py-2 px-2">Randomness and Sensitivity</td>
          </tr>
        </tbody>
      </table>

      {/* Summary row */}
      <div className="flex items-center gap-6 pt-3 border-t border-neutral-200 dark:border-neutral-700">
        <div>
          <div className="text-xs text-neutral-400 dark:text-neutral-500 mb-0.5">T + D + G</div>
          <div className="font-mono font-bold text-neutral-800 dark:text-neutral-100">₹{total_pde_price.toFixed(2)}</div>
        </div>
        <div className="text-neutral-300 dark:text-neutral-600 text-lg">≈</div>
        <div>
          <div className="text-xs text-neutral-400 dark:text-neutral-500 mb-0.5">Actual Price</div>
          <div className="font-mono font-bold text-neutral-800 dark:text-neutral-100">₹{actual_option_price.toFixed(2)}</div>
        </div>
        <div className="ml-auto text-right">
          <div className="text-xs text-neutral-400 dark:text-neutral-500 mb-0.5">Error</div>
          <div className={`font-mono font-semibold text-sm ${pde_error < 0.5 ? 'text-green-600' : 'text-orange-500'}`}>
            ±₹{pde_error.toFixed(2)}{errorPct && <span className="text-[10px] ml-0.5">({errorPct}%)</span>}
          </div>
        </div>
      </div>
    </Card>
  );
}

// ─── Sub-component: Greeks Cards ──────────────────────────────────────────────

const GREEK_META = {
  delta: { label: 'Δ Delta', color: 'border-primary-400', bg: 'bg-surface', text: 'text-primary-700 dark:text-primary-300', desc: 'Price change per ₹1 move', showBar: true },
  gamma: { label: 'Γ Gamma', color: 'border-success-400', bg: 'bg-surface', text: 'text-success-700 dark:text-success-300', desc: 'Rate of delta change per ₹1', showBar: false },
  theta: { label: 'Θ Theta', color: 'border-danger-400', bg: 'bg-surface', text: 'text-danger-700 dark:text-danger-300', desc: 'Daily time decay (₹/day)', showBar: false },
  vega:  { label: 'ν Vega',  color: 'border-secondary-400', bg: 'bg-surface', text: 'text-secondary-700 dark:text-secondary-300', desc: 'Price change per 1% IV move', showBar: false },
  rho:   { label: 'ρ Rho',   color: 'border-warning-400', bg: 'bg-surface', text: 'text-warning-700 dark:text-warning-300', desc: 'Price change per 1% rate move', showBar: false },
};

function GreekCell({ greek, value, interpretation }) {
  const cfg = GREEK_META[greek];
  if (!cfg || value === undefined) return null;
  return (
    <div className={`border-l-4 ${cfg.color} ${cfg.bg} rounded-r-clay-sm px-3 py-2 h-full shadow-clay-btn dark:shadow-clay-btn-dark flex items-center justify-between gap-3`}>
      {/* Left: label + value */}
      <div className="min-w-0">
        <div className={`text-xs font-bold ${cfg.text} mb-0.5`}>{cfg.label}</div>
        <div className={`text-lg font-bold ${cfg.text} font-mono leading-tight`}>
          {value.toFixed(greek === 'gamma' ? 6 : 4)}
        </div>
        {greek === 'delta' && (
          <div className="mt-1.5">
            <div className="w-full bg-neutral-200 dark:bg-white/10 rounded-full h-1">
              <div className="bg-primary-500 h-1 rounded-full" style={{ width: `${Math.abs(value) * 100}%` }} />
            </div>
          </div>
        )}
        {interpretation && (
          <div className="text-[10px] text-neutral-500 dark:text-neutral-400 mt-1 italic leading-tight">
            {interpretation}
          </div>
        )}
      </div>
      {/* Right: meaning */}
      <div className="text-[10px] text-neutral-400 dark:text-neutral-500 text-right leading-snug shrink-0 max-w-[45%]">
        {cfg.desc}
      </div>
    </div>
  );
}

const COL_LABELS = ['Stock / Index Price', 'Volatility', 'Interest Rate / Time'];

function GreeksCards({ greeks, interpretations }) {
  return (
    <div className="grid grid-cols-3 gap-3">
      {/* Col 1: Delta + Gamma */}
      <div className="flex flex-col gap-2">
        <p className="text-[10px] font-semibold uppercase tracking-widest text-neutral-400 dark:text-neutral-500">{COL_LABELS[0]}</p>
        <GreekCell greek="delta" value={greeks.delta} interpretation={interpretations?.delta} />
        <GreekCell greek="gamma" value={greeks.gamma} interpretation={interpretations?.gamma} />
      </div>

      {/* Col 2: Vega */}
      <div className="flex flex-col gap-2">
        <p className="text-[10px] font-semibold uppercase tracking-widest text-neutral-400 dark:text-neutral-500">{COL_LABELS[1]}</p>
        <GreekCell greek="vega" value={greeks.vega} interpretation={interpretations?.vega} />
      </div>

      {/* Col 3: Theta + Rho */}
      <div className="flex flex-col gap-2">
        <p className="text-[10px] font-semibold uppercase tracking-widest text-neutral-400 dark:text-neutral-500">{COL_LABELS[2]}</p>
        <GreekCell greek="theta" value={greeks.theta} interpretation={interpretations?.theta} />
        <GreekCell greek="rho"   value={greeks.rho}   interpretation={interpretations?.rho}   />
      </div>
    </div>
  );
}

// ─── Sub-component: Moneyness Card ────────────────────────────────────────────

function MoneynessCard({ moneyness }) {
  const { value, is_itm, intrinsic_value, extrinsic_value } = moneyness;
  return (
    <Card title="Moneyness">
      <div className="grid grid-cols-2 gap-3">
        {[
          ['Status', is_itm ? 'In The Money' : 'Out of The Money', is_itm ? 'text-success-600' : 'text-neutral-500 dark:text-neutral-400'],
          ['Strike vs Spot', `${value >= 0 ? '+' : ''}${value.toFixed(2)}%`, value >= 0 ? 'text-orange-600' : 'text-primary-600'],
          ['Intrinsic Value', `₹${intrinsic_value.toFixed(2)}`, 'text-neutral-800 dark:text-neutral-200'],
          ['Time Value', `₹${extrinsic_value.toFixed(2)}`, 'text-neutral-800 dark:text-neutral-200'],
        ].map(([label, val, cls]) => (
          <div key={label} className="text-center p-2 rounded-clay-sm bg-neutral-50 dark:bg-white/5 shadow-clay-btn dark:shadow-clay-btn-dark">
            <div className="text-xs text-neutral-500 dark:text-neutral-400 mb-1">{label}</div>
            <div className={`text-sm font-bold ${cls}`}>{val}</div>
          </div>
        ))}
      </div>
    </Card>
  );
}

// ─── Sub-component: PDE Educational Section ───────────────────────────────────

function PDEExplainer() {
  const [open, setOpen] = useState(false);
  return (
    <Card>
      <div className="flex items-center justify-between cursor-pointer" onClick={() => setOpen(!open)}>
        <div>
          <h3 className="text-base font-semibold text-neutral-900 dark:text-white">
            What is PDE Price Decomposition?
          </h3>
          <p className="text-xs text-neutral-500 dark:text-neutral-400 mt-0.5">
            Breaking down option price into Time Decay, Drift, and Convexity
          </p>
        </div>
        <span className="text-neutral-400 dark:text-neutral-500 ml-4 text-lg select-none">
          {open ? '▲' : '▼'}
        </span>
      </div>

      {open && (
        <div className="mt-4 space-y-4 text-sm text-neutral-700 dark:text-neutral-300">
          <div className="p-3 bg-neutral-50 dark:bg-white/5 rounded-clay-sm font-mono text-xs leading-relaxed">
            <div className="text-neutral-500 dark:text-neutral-400 mb-1">Black-Scholes PDE:</div>
            <div>rV = ∂V/∂t + rS·∂V/∂S + ½σ²S²·∂²V/∂S²</div>
            <div className="mt-1 text-neutral-400">Divide by r → V = T + D + G</div>
          </div>

          <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
            <div className="p-3 bg-red-50 dark:bg-red-900/20 rounded-clay-sm">
              <div className="font-semibold text-red-700 dark:text-red-300 mb-1">T — Time Decay</div>
              <div className="font-mono text-xs text-red-600 dark:text-red-400 mb-1">T = (1/r) × Θ_annual</div>
              <ul className="text-xs text-neutral-600 dark:text-neutral-400 space-y-1 list-disc list-inside">
                <li>Always negative — time erodes value</li>
                <li>Sellers collect this premium daily</li>
                <li>Accelerates near expiry (Theta bleed)</li>
              </ul>
            </div>
            <div className="p-3 bg-blue-50 dark:bg-blue-900/20 rounded-clay-sm">
              <div className="font-semibold text-blue-700 dark:text-blue-300 mb-1">D — Drift</div>
              <div className="font-mono text-xs text-blue-600 dark:text-blue-400 mb-1">D = S × Δ</div>
              <ul className="text-xs text-neutral-600 dark:text-neutral-400 space-y-1 list-disc list-inside">
                <li>Value from underlying price movement</li>
                <li>Scales with spot price and delta</li>
                <li>Buyers need this to overcome Theta</li>
              </ul>
            </div>
            <div className="p-3 bg-green-50 dark:bg-green-900/20 rounded-clay-sm">
              <div className="font-semibold text-green-700 dark:text-green-300 mb-1">G — Convexity</div>
              <div className="font-mono text-xs text-green-600 dark:text-green-400 mb-1">G = (σS)²Γ / 2r</div>
              <ul className="text-xs text-neutral-600 dark:text-neutral-400 space-y-1 list-disc list-inside">
                <li>Value from volatility — the "gamma profit"</li>
                <li>Grows when the stock moves sharply</li>
                <li>Why options are non-linear instruments</li>
              </ul>
            </div>
          </div>

          <div className="p-3 bg-amber-50 dark:bg-amber-900/20 rounded-clay-sm text-xs text-amber-800 dark:text-amber-300">
            <span className="font-semibold">Why T + D + G ≈ V (not exactly)?</span>{' '}
            The PDE holds instantaneously in continuous time. Discrete daily data, rounding, and
            the fact that Greeks change as time passes mean the sum has small errors — typically
            under 5% of the option price.
          </div>
        </div>
      )}
    </Card>
  );
}

// ─── Chart Section ────────────────────────────────────────────────────────────

function ChartSection({ histData, histLoading, histError, symbol, strike, optionType }) {
  if (histLoading) {
    return (
      <div className="grid grid-cols-1 gap-6">
        {[1, 2, 3].map((i) => (
          <div key={i} className="bg-surface border border-surface rounded-clay p-5 h-64 animate-pulse shadow-clay dark:shadow-clay-dark" />
        ))}
      </div>
    );
  }

  if (histError || !histData || histData.data?.length === 0) {
    return (
      <div className="p-4 bg-amber-50 dark:bg-amber-900/20 border border-amber-200/60 dark:border-amber-800/60 rounded-clay text-sm text-amber-700 dark:text-amber-400 shadow-clay dark:shadow-clay-dark">
        {histError || 'No historical data available for this contract.'}
        <span className="block text-xs mt-1 opacity-70">
          Run the backfill command to populate 20+ days of option chain data.
        </span>
      </div>
    );
  }

  const { data, days_available } = histData;
  const dataLabel = `${days_available} trading day${days_available !== 1 ? 's' : ''}`;

  return (
    <div className="space-y-4">
      {/* PDE waterfall — first */}
      <Card title={`PDE Components over ${dataLabel} — ${strike} ${optionType}`}>
        <p className="text-xs text-neutral-400 dark:text-neutral-500 mb-3">
          Each bar starts at the previous day's close. T (red) = time decay, D (blue) = delta × ΔSpot, G (green) = ½Γ·ΔSpot², R (gray) = residual (IV change + higher-order). Stack reconciles to actual close.
        </p>
        <PDEStackedChart data={data} height={460} syncId="option-charts" />
      </Card>

      {/* Option price + Spot price — side by side */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        <Card title={`${symbol} ${strike} ${optionType} Option Price — ${dataLabel}`}>
          <p className="text-xs text-neutral-400 dark:text-neutral-500 mb-3">
            Daily candlestick of the option's open, high, low and close price.
          </p>
          <OptionPriceChart data={data} height={260} syncId="option-charts" />
        </Card>

        <Card title={`${symbol} Spot Price — ${dataLabel}`}>
          <p className="text-xs text-neutral-400 dark:text-neutral-500 mb-3">
            Underlying close price on each trading day.
          </p>
          <SpotPriceChart data={data} symbol={symbol} strike={strike} height={260} syncId="option-charts" />
        </Card>
      </div>

      {/* Delta & Gamma */}
      <Card title={`Delta & Gamma — ${dataLabel}`}>
        <p className="text-xs text-neutral-400 dark:text-neutral-500 mb-3">
          Delta (blue, left axis) and Gamma (green, right axis) over time.
        </p>
        <DualAxisLineChart
          data={data}
          leftKey="delta"
          rightKey="gamma"
          leftLabel="Δ Delta"
          rightLabel="Γ Gamma"
          leftColor="#3b82f6"
          rightColor="#22c55e"
          leftFormat={(v) => v.toFixed(2)}
          rightFormat={(v) => v.toFixed(5)}
          height={200}
        />
      </Card>

      {/* Theta & Vega */}
      <Card title={`Theta & Vega — ${dataLabel}`}>
        <p className="text-xs text-neutral-400 dark:text-neutral-500 mb-3">
          Theta (red, left axis — daily decay) and Vega (purple, right axis — per 1% IV move).
        </p>
        <DualAxisLineChart
          data={data}
          leftKey="theta"
          rightKey="vega"
          leftLabel="Θ Theta (₹/day)"
          rightLabel="ν Vega (₹/1% IV)"
          leftColor="#ef4444"
          rightColor="#a855f7"
          leftFormat={(v) => v.toFixed(2)}
          rightFormat={(v) => v.toFixed(3)}
          zeroLine={true}
          height={200}
        />
      </Card>
    </div>
  );
}

// ─── Main Page Component ──────────────────────────────────────────────────────

const BREAKDOWN_SYMBOLS = [
  { value: 'NIFTY',     label: 'Nifty 50',     type: 'index' },
  { value: 'BANKNIFTY', label: 'Bank Nifty',   type: 'index' },
  { value: 'FINNIFTY',  label: 'Fin Nifty',    type: 'index' },
  { value: 'RELIANCE',  label: 'Reliance',     type: 'stock' },
];

export default function OptionsBreakdown() {
  const [symbol, setSymbol] = useState('RELIANCE');

  const [availableDates, setAvailableDates] = useState([]);
  const [selectedDate, setSelectedDate] = useState('');
  const [availableStrikes, setAvailableStrikes] = useState([]);
  const [selectedStrike, setSelectedStrike] = useState('');
  const [atmStrike, setAtmStrike] = useState(null);
  const [spotPrice, setSpotPrice] = useState(null);
  const [expiryDate, setExpiryDate] = useState(null);
  const [optionType, setOptionType] = useState('CE');
  const [breakdown, setBreakdown] = useState(null);
  const [pde, setPde] = useState(null);
  const [histData, setHistData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [histLoading, setHistLoading] = useState(false);
  const [datesLoading, setDatesLoading] = useState(true);
  const [strikesLoading, setStrikesLoading] = useState(false);
  const [error, setError] = useState('');
  const [histError, setHistError] = useState('');
  const [activeTab, setActiveTab] = useState('charts');
  const tabsRef = useRef(null);
  const prevBreakdownRef = useRef(null);

  const symbolLabel = BREAKDOWN_SYMBOLS.find((s) => s.value === symbol)?.label ?? symbol;

  // Scroll tabs into view only when results first appear (null → data)
  useEffect(() => {
    if (breakdown && prevBreakdownRef.current === null && tabsRef.current) {
      const top = tabsRef.current.getBoundingClientRect().top + window.scrollY - 80;
      window.scrollTo({ top, behavior: 'smooth' });
    }
    prevBreakdownRef.current = breakdown;
  }, [breakdown]);

  // Fetch available dates whenever symbol changes
  useEffect(() => {
    setDatesLoading(true);
    setAvailableDates([]);
    setSelectedDate('');
    setBreakdown(null);
    setPde(null);
    setHistData(null);
    setError('');
    setHistError('');

    fetch(`${API}/api/option-breakdown/dates/${symbol}/`)
      .then((r) => r.json())
      .then((data) => {
        if (data.dates && data.dates.length > 0) {
          setAvailableDates(data.dates);
          setSelectedDate(data.dates[0]);
        } else {
          setError(`No option chain data for ${symbol}. Run: python manage.py backfill_option_chain --symbol ${symbol}`);
        }
      })
      .catch(() => setError('Failed to load dates. Is the backend running?'))
      .finally(() => setDatesLoading(false));
  }, [symbol]);

  // Fetch strikes when date changes
  useEffect(() => {
    if (!selectedDate) return;
    setStrikesLoading(true);
    setBreakdown(null);
    setPde(null);
    setHistData(null);
    setError('');
    setHistError('');

    fetch(`${API}/api/option-breakdown/strikes/${symbol}/${selectedDate}/`)
      .then((r) => r.json())
      .then((data) => {
        setAvailableStrikes(data.strikes || []);
        setSpotPrice(data.spot_price || null);
        setAtmStrike(data.atm_strike || null);
        setExpiryDate(data.expiry_date || null);
        setSelectedStrike(String(data.atm_strike || ''));
      })
      .catch(() => setError('Failed to load strikes.'))
      .finally(() => setStrikesLoading(false));
  }, [selectedDate, symbol]);

  const fetchBreakdown = useCallback(() => {
    if (!selectedDate || !selectedStrike) return;
    setLoading(true);
    setHistLoading(true);
    setError('');
    setHistError('');

    // Fetch current breakdown and historical data in parallel
    const breakdownUrl = `${API}/api/option-breakdown/${symbol}/${selectedDate}/${selectedStrike}/${optionType}/`;
    const histUrl = `${API}/api/option-breakdown/historical/${symbol}/${selectedStrike}/${optionType}/?days=20`;

    fetch(breakdownUrl)
      .then(async (r) => {
        const json = await r.json();
        if (!r.ok) throw new Error(json.error || 'Request failed');
        return json;
      })
      .then((data) => {
        setBreakdown(data);
        // Compute PDE from breakdown data client-side
        const { greeks, inputs } = data;
        const { delta, gamma, theta } = greeks;
        const { risk_free_rate: r, volatility: sigma, spot: S } = inputs;
        if (r > 0) {
          const thetaAnnual = theta * 365;
          const T = (1 / r) * thetaAnnual;
          const D = S * delta;
          const G = (sigma * S) ** 2 * gamma / (2 * r);
          const total = T + D + G;
          const actualPrice = data.theoretical_price;
          setPde({
            time_decay_component: parseFloat(T.toFixed(4)),
            drift_component: parseFloat(D.toFixed(4)),
            convexity_component: parseFloat(G.toFixed(4)),
            total_pde_price: parseFloat(total.toFixed(4)),
            actual_option_price: actualPrice,
            pde_error: parseFloat(Math.abs(actualPrice - total).toFixed(4)),
            // raw inputs for showing the actual calculation
            _r: r, _sigma: sigma, _S: S,
            _delta: delta, _gamma: gamma, _thetaAnnual: thetaAnnual,
          });
        }
      })
      .catch((e) => setError(e.message))
      .finally(() => setLoading(false));

    fetch(histUrl)
      .then(async (r) => {
        const json = await r.json();
        if (!r.ok) throw new Error(json.error || 'Historical data unavailable');
        return json;
      })
      .then(setHistData)
      .catch((e) => setHistError(e.message))
      .finally(() => setHistLoading(false));
  }, [selectedDate, selectedStrike, optionType, symbol]);

  return (
    <>
      <SEOHead
        canonicalPath="/options-breakdown"
        title={`Option Analysis — ${symbol} Greeks & PDE | OptionView`}
        description={`Full Black-Scholes breakdown with Delta, Gamma, Theta, Vega and Rho for ${symbolLabel} options. PDE decomposition and 20-day trend charts.`}
        keywords={`options greeks calculator, ${symbol} options, delta gamma theta vega, Black-Scholes India, PDE decomposition`}
      />

      <div className="max-w-7xl mx-auto px-4 sm:px-6 pt-4">
        <Link to="/" className="inline-flex items-center gap-1 text-xs text-neutral-400 dark:text-neutral-500 hover:text-primary-600 dark:hover:text-primary-400 transition-colors mb-2">
          <svg className="w-3.5 h-3.5" fill="none" stroke="currentColor" strokeWidth={2} viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" d="M15 19l-7-7 7-7" /></svg>
          Back to Home
        </Link>
      </div>

      <div className="max-w-7xl mx-auto px-4 sm:px-6 py-6 space-y-6">

        {/* Page header */}
        <div>
          <h1 className="text-2xl sm:text-3xl font-bold text-neutral-900 dark:text-white">
            Option Analysis &amp; Greeks
          </h1>
          <p className="text-neutral-500 dark:text-neutral-400 mt-1 text-sm">
            Black-Scholes analysis with PDE decomposition for{' '}
            <span className="font-semibold text-primary-700 dark:text-primary-400">{symbolLabel}</span>.
            Select a date and strike to see the full breakdown and 20-day charts.
          </p>
        </div>

        {/* ── ROW 1: Controls ── */}
        <Card>
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4 items-end">
            {/* Symbol selector */}
            <div>
              <label className="block text-xs font-medium text-neutral-500 dark:text-neutral-400 mb-1">Symbol</label>
              <select
                value={symbol}
                onChange={(e) => setSymbol(e.target.value)}
                className="input-base focus-ring h-9"
              >
                {BREAKDOWN_SYMBOLS.map((s) => (
                  <option key={s.value} value={s.value}>{s.label}</option>
                ))}
              </select>
            </div>

            {/* Date selector */}
            <div>
              <label className="block text-xs font-medium text-neutral-500 dark:text-neutral-400 mb-1">Trade Date</label>
              {datesLoading ? (
                <div className="h-9 bg-neutral-100 dark:bg-white/8 rounded-clay-sm animate-pulse" />
              ) : (
                <select
                  value={selectedDate}
                  onChange={(e) => setSelectedDate(e.target.value)}
                  className="input-base focus-ring h-9"
                >
                  {availableDates.map((d) => (
                    <option key={d} value={d}>{d}</option>
                  ))}
                </select>
              )}
            </div>

            {/* Strike selector */}
            <div>
              <label className="block text-xs font-medium text-neutral-500 dark:text-neutral-400 mb-1">
                Strike{atmStrike && <span className="ml-1 text-primary-500 dark:text-primary-400">(ATM: {atmStrike})</span>}
              </label>
              {strikesLoading ? (
                <div className="h-9 bg-neutral-100 dark:bg-white/8 rounded-clay-sm animate-pulse" />
              ) : (
                <select
                  value={selectedStrike}
                  onChange={(e) => setSelectedStrike(e.target.value)}
                  className="input-base focus-ring h-9"
                >
                  {availableStrikes.map((s) => (
                    <option key={s} value={String(s)}>
                      {s}{s === atmStrike ? ' (ATM)' : ''}
                    </option>
                  ))}
                </select>
              )}
            </div>

            {/* Option type + calculate */}
            <div>
              <label className="block text-xs font-medium text-neutral-500 dark:text-neutral-400 mb-1">Option Type</label>
              <div className="flex gap-2">
                <div className="flex rounded-clay-sm border border-neutral-200 dark:border-transparent overflow-hidden flex-1 shadow-clay-btn dark:shadow-clay-btn-dark">
                  {['CE', 'PE'].map((type) => (
                    <button
                      key={type}
                      onClick={() => setOptionType(type)}
                      className={`flex-1 text-sm font-medium py-1.5 transition-all duration-150 ${
                        optionType === type
                          ? 'bg-primary-600 text-white'
                          : 'bg-surface text-neutral-600 dark:text-neutral-300 hover:bg-neutral-50 dark:hover:bg-white/8'
                      }`}
                    >
                      {type}
                    </button>
                  ))}
                </div>
                <button
                  onClick={fetchBreakdown}
                  disabled={loading || !selectedStrike}
                  className="px-4 py-1.5 bg-primary-600 text-white text-sm font-medium rounded-clay-sm hover:bg-primary-700 active:bg-primary-800 disabled:opacity-50 disabled:cursor-not-allowed transition-all duration-150 shadow-clay-btn dark:shadow-clay-btn-dark active:translate-y-px"
                >
                  {loading ? '…' : 'Calculate'}
                </button>
              </div>
            </div>
          </div>

          {(spotPrice || expiryDate) && (
            <div className="mt-3 flex flex-wrap gap-x-6 gap-y-1 text-xs text-neutral-500 dark:text-neutral-400">
              {spotPrice && (
                <span>Spot on {selectedDate}:{' '}
                  <span className="font-semibold text-neutral-700 dark:text-neutral-200">₹{spotPrice.toFixed(2)}</span>
                </span>
              )}
              {expiryDate && (
                <span>Expiry:{' '}
                  <span className="font-semibold text-orange-600 dark:text-orange-400">{expiryDate}</span>
                </span>
              )}
            </div>
          )}
        </Card>

        {/* Error banner */}
        {error && (
          <div className="p-4 bg-danger-50/80 dark:bg-danger-900/20 border border-danger-200/60 dark:border-danger-800/60 rounded-clay text-sm text-danger-700 dark:text-danger-400 shadow-clay dark:shadow-clay-dark">
            {error}
          </div>
        )}

        {/* Loading skeleton — first load only */}
        {loading && !breakdown && (
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
            {[1, 2, 3, 4].map((i) => (
              <div key={i} className="bg-surface border border-surface rounded-clay p-5 h-48 animate-pulse shadow-clay dark:shadow-clay-dark" />
            ))}
          </div>
        )}

        {/* Results — kept mounted during re-fetch, dimmed while loading */}
        {breakdown && (
          <div className={loading ? 'opacity-50 pointer-events-none transition-opacity duration-150' : 'transition-opacity duration-150'}>
            {/* ── Tabs ── */}
            <div ref={tabsRef} className="bg-surface border border-surface rounded-clay shadow-clay dark:shadow-clay-dark overflow-hidden">

              {/* Tab bar */}
              <div className="flex border-b border-neutral-100 dark:border-transparent">
                {[
                  { id: 'charts',  label: 'Price Change Analysis' },
                  { id: 'greeks',  label: 'Option Statistics' },
                ].map(({ id, label }) => (
                  <button
                    key={id}
                    onClick={() => setActiveTab(id)}
                    className={`px-6 py-3 text-sm font-medium transition-all duration-150 border-b-2 -mb-px ${
                      activeTab === id
                        ? 'border-primary-500 text-primary-700 dark:text-primary-300'
                        : 'border-transparent text-neutral-500 dark:text-neutral-400 hover:text-neutral-700 dark:hover:text-neutral-200'
                    }`}
                  >
                    {label}
                  </button>
                ))}
              </div>

              {/* Tab: Charts */}
              {activeTab === 'charts' && (
                <div className="p-4">
                  <ChartSection
                    histData={histData}
                    histLoading={histLoading}
                    histError={histError}
                    symbol={symbol}
                    strike={selectedStrike}
                    optionType={optionType}
                  />
                </div>
              )}

              {/* Tab: Option Statistics */}
              {activeTab === 'greeks' && (
                <div className="p-4 space-y-4">
                  {/* Row 1: Greeks summary cards */}
                  <div>
                    <SectionTitle>Greeks</SectionTitle>
                    <GreeksCards
                      greeks={breakdown.greeks}
                      interpretations={breakdown.interpretations}
                    />
                  </div>

                  {/* Row 2: Option Price Decomposition (left) + Option Statistics (right) */}
                  <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
                    {/* Left — PDE table only, no equation */}
                    <PDECard pde={pde} showEquation={false} />

                    {/* Right — key option stats: T, IV, HV */}
                    <Card title="Option Statistics">
                      {breakdown.theoretical_price_hv != null ? (
                        <div className="mb-4">
                          <div className="text-xs font-semibold text-neutral-500 dark:text-neutral-400 uppercase tracking-wide mb-2">
                            Theoretical vs Market (30d HV = {breakdown.historical_volatility?.toFixed(1)}%)
                          </div>
                          <div className="grid grid-cols-3 gap-3">
                            <div className="text-center p-3 bg-emerald-50 dark:bg-emerald-900/20 rounded-clay-sm shadow-clay-btn dark:shadow-clay-btn-dark">
                              <div className="text-xs text-emerald-600 dark:text-emerald-400 font-medium mb-1">Theoretical (HV)</div>
                              <div className="text-xl font-bold text-emerald-700 dark:text-emerald-300">₹{breakdown.theoretical_price_hv.toFixed(2)}</div>
                            </div>
                            <div className="text-center p-3 bg-neutral-50 dark:bg-white/5 rounded-clay-sm shadow-clay-btn dark:shadow-clay-btn-dark">
                              <div className="text-xs text-neutral-500 dark:text-neutral-400 font-medium mb-1">Market Price</div>
                              <div className="text-xl font-bold text-neutral-800 dark:text-neutral-100">₹{breakdown.market_price.toFixed(2)}</div>
                            </div>
                            <div className="text-center p-3 bg-neutral-50 dark:bg-white/5 rounded-clay-sm shadow-clay-btn dark:shadow-clay-btn-dark">
                              <div className="text-xs text-neutral-500 dark:text-neutral-400 font-medium mb-1">Difference</div>
                              <div className={`text-xl font-bold ${Math.abs(breakdown.price_difference_hv) < 2 ? 'text-green-600' : breakdown.price_difference_hv > 0 ? 'text-orange-600' : 'text-blue-600'}`}>
                                {breakdown.price_difference_hv >= 0 ? '+' : ''}₹{breakdown.price_difference_hv.toFixed(2)}
                              </div>
                            </div>
                          </div>
                        </div>
                      ) : (
                        <div className="mb-4 p-3 bg-amber-50 dark:bg-amber-900/20 rounded-clay-sm text-xs text-amber-700 dark:text-amber-400 shadow-clay-btn dark:shadow-clay-btn-dark">
                          Insufficient price history to compute 30-day HV (need at least 31 trading days).
                        </div>
                      )}
                      <table className="w-full text-sm">
                        <tbody className="divide-y divide-neutral-100 dark:divide-white/8">
                          {[
                            ['T — Time to Expiry', `${breakdown.inputs.time_to_expiry_days.toFixed(1)} days (${breakdown.inputs.time_to_expiry_years.toFixed(4)} yrs)`],
                            ['σ — Implied Volatility', `${(breakdown.inputs.volatility * 100).toFixed(2)}%`],
                            ['σ_HV — 30d Historical Vol', breakdown.historical_volatility != null ? `${breakdown.historical_volatility.toFixed(2)}%` : 'N/A'],
                          ].map(([label, value]) => (
                            <tr key={label}>
                              <td className="py-1.5 text-neutral-500 dark:text-neutral-400 font-mono text-xs">{label}</td>
                              <td className="py-1.5 text-neutral-800 dark:text-neutral-200 font-medium text-right">{value}</td>
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    </Card>
                  </div>

                  {/* Glossary — everything else */}
                  <div>
                    <SectionTitle>Glossary</SectionTitle>
                    <div className="space-y-6">
                      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
                        <BlackScholesBreakdown data={breakdown} />
                        <div className="space-y-6">
                          <MoneynessCard moneyness={breakdown.moneyness} />
                          <Card title="Market Data">
                            <table className="w-full text-sm">
                              <tbody className="divide-y divide-neutral-100 dark:divide-white/8">
                                {[
                                  ['Open',   breakdown.market_data.open   ? `₹${breakdown.market_data.open.toFixed(2)}`   : '—'],
                                  ['High',   breakdown.market_data.high   ? `₹${breakdown.market_data.high.toFixed(2)}`   : '—'],
                                  ['Low',    breakdown.market_data.low    ? `₹${breakdown.market_data.low.toFixed(2)}`    : '—'],
                                  ['Close',  `₹${breakdown.market_data.close.toFixed(2)}`],
                                  ['Volume', breakdown.market_data.volume ? breakdown.market_data.volume.toLocaleString() : '—'],
                                  ['OI',     breakdown.market_data.open_interest ? breakdown.market_data.open_interest.toLocaleString() : '—'],
                                ].map(([label, value]) => (
                                  <tr key={label}>
                                    <td className="py-1.5 text-neutral-500 dark:text-neutral-400">{label}</td>
                                    <td className="py-1.5 text-neutral-800 dark:text-neutral-200 font-medium text-right">{value}</td>
                                  </tr>
                                ))}
                              </tbody>
                            </table>
                          </Card>
                        </div>
                      </div>
                      <PDEExplainer />
                    </div>
                  </div>
                </div>
              )}
            </div>

            <div className="text-xs text-neutral-400 dark:text-neutral-500 text-center pb-2">
              Data from NSE Bhavcopy · Greeks computed via Black-Scholes
            </div>
          </div>
        )}

        <AdSense adSlot="" label="Options Breakdown Banner" adStyle={{ minHeight: 90 }} />

        {/* Empty state */}
        {!breakdown && !loading && !error && !datesLoading && (
          <div className="text-center py-20 text-neutral-400">
            <div className="mb-3 flex justify-center">
              <svg className="w-10 h-10 text-neutral-300 dark:text-neutral-600" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={1.5}>
                <path strokeLinecap="round" strokeLinejoin="round" d="M3 13.125C3 12.504 3.504 12 4.125 12h2.25c.621 0 1.125.504 1.125 1.125v6.75C7.5 20.496 6.996 21 6.375 21h-2.25A1.125 1.125 0 013 19.875v-6.75zM9.75 8.625c0-.621.504-1.125 1.125-1.125h2.25c.621 0 1.125.504 1.125 1.125v11.25c0 .621-.504 1.125-1.125 1.125h-2.25a1.125 1.125 0 01-1.125-1.125V8.625zM16.5 4.125c0-.621.504-1.125 1.125-1.125h2.25C20.496 3 21 3.504 21 4.125v15.75c0 .621-.504 1.125-1.125 1.125h-2.25a1.125 1.125 0 01-1.125-1.125V4.125z" />
              </svg>
            </div>
            <p className="text-sm">Select a date and strike, then click Calculate.</p>
          </div>
        )}
      </div>
    </>
  );
}
